import { Text, Group, Button, Checkbox, Stack, Alert, Box } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { ErrorText } from "../../../blocks/errors/error-alert"
import { usePostMutation } from "../../../typed-client/hooks/use-post-mutation"
import { LinkAnchor } from "../../app/-components/link-anchor"
import { DefaultUserNameTextInput } from "../../../-components/default-username-text-input"
import { DefaultPasswordInput } from "../../../-components/default-password-input"
import { DefaultError } from "../../../-components/error-context"
import { useInterval, useLocalStorage } from "@mantine/hooks"
import SsoLoginButtons from "./SsoLoginButtons"

export function LoginForm() {
  const queryClient = useQueryClient()
  const [savedPersistence, setSavedPersistence] = useLocalStorage<
    boolean | null
  >({
    defaultValue: null,
    key: "login-persistence-choice",
  })
  const form = useForm({
    initialValues: {
      userName: "",
      password: "",
      persistent: false,
    },
  })
  const search = useSearch({ from: "/authenticate/login" })

  const navigate = useNavigate()
  const { t } = useTranslation("app_server")

  const [lockSeconds, setLockSeconds] = React.useState<number | null>(null)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [dontAskAgain, setDontAskAgain] = React.useState(false)
  const [pendingLogin, setPendingLogin] = React.useState<
    ((persistent: boolean) => void) | null
  >(null)

  const { start, stop } = useInterval(() => {
    if (lockSeconds !== null && lockSeconds > 0) setLockSeconds(lockSeconds - 1)
    if (lockSeconds === 0) {
      stop()
      setLockSeconds(null)
    }
  }, 1000)
  const [displayTime, setDisplayTime] = React.useState<string>("")

  React.useEffect(() => {
    if (lockSeconds) {
      const time =
        Math.floor(lockSeconds / 3600)
          .toString()
          .padStart(2, "0") +
        ":" +
        Math.floor((lockSeconds % 3600) / 60)
          .toString()
          .padStart(2, "0") +
        ":" +
        (lockSeconds % 60).toString().padStart(2, "0")
      setDisplayTime(time)
    }
  }, [lockSeconds])

  const {
    mutateAsync: login,
    isPending,
    isError,
  } = usePostMutation("/api/Authentication/Login", {
    onSuccess: (loginResponse) => {
      queryClient.clear()
      if (loginResponse.mfaRequired) {
        form.reset()
        if (loginResponse.mfaConfiguered) {
          navigate({
            to: "/authenticate/mfa/select-authentication-factor",
            search: { returnUrl: search.returnUrl },
          })
        } else {
          navigate({
            to: "/authenticate/mfa/create",
            search: { returnUrl: search.returnUrl },
          })
        }
      } else {
        notifications.show({
          color: "green",
          title: t("Success"),
          message: t("Authenticate_LoginForm_SuccessMessage"),
        })
        navigate({ to: search.returnUrl || "/app" })
      }
    },
    onError: (error: DefaultError) => {
      setErrorMessage(error.message)
      if (error.LockSeconds) {
        setLockSeconds(error.LockSeconds)

        start()
      }
    },
  })

  const requestPersistence = (action: (persistent: boolean) => void) => {
    if (savedPersistence !== null) {
      action(savedPersistence)
      return
    }

    setPendingLogin(() => action)
  }

  const selectPersistence = (persistent: boolean) => {
    if (dontAskAgain) setSavedPersistence(persistent)
    pendingLogin?.(persistent)
    setPendingLogin(null)
    setDontAskAgain(false)
  }

  const handleSubmit = (values: typeof form.values) => {
    requestPersistence((persistent) => {
      void login({ ...values, persistent })
    })
  }

  const handleSsoLogin = (providerId: number) => {
    requestPersistence((persistent) => {
      const returnUrl = encodeURIComponent(window.location.pathname)
      window.location.assign(
        `/api/sso/login/${providerId}?returnUrl=${returnUrl}&persistent=${persistent}`
      )
    })
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xs" maw={400}>
        {pendingLogin ? (
          <>
            <Text fw={600}>{t("Login_RememberMe")}</Text>

            <Stack>
              <Button type="button" onClick={() => selectPersistence(true)}>
                {t("Yes")}
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() => selectPersistence(false)}
              >
                {t("No")}
              </Button>
              <Checkbox
                checked={dontAskAgain}
                onChange={(event) =>
                  setDontAskAgain(event.currentTarget.checked)
                }
                label={t("Login_DontAskAgain")}
              />
            </Stack>
          </>
        ) : (
          <>
            <DefaultUserNameTextInput
              autoFocus
              required
              {...form.getInputProps("userName")}
            />
            <Box>
              <DefaultPasswordInput {...form.getInputProps("password")} />
              <LinkAnchor
                fz="sm"
                c="dimmed"
                to={"/authenticate/password-forgotten"}
                search={{ userName: form.values.userName }}
              >
                {t("ResetPassword")}
              </LinkAnchor>
            </Box>
            {isError ? <ErrorText mt="xs" error={errorMessage} /> : null}
            {lockSeconds ? (
              <Text c="red">
                {t("Login_Lock_Message", { time: displayTime })}
              </Text>
            ) : null}
            <Button type="submit" fullWidth loading={isPending}>
              {t("Login")}
            </Button>{" "}
            <SsoLoginButtons tenantId={undefined} onLogin={handleSsoLogin} />
            <LoginError error={search.error} />
          </>
        )}
      </Stack>
    </form>
  )
}

function LoginError({ error }: { error?: string }) {
  const { t } = useTranslation("app_server")
  if (!error) return null

  const message = (() => {
    switch (error) {
      case "authentication_failed":
        return t("Login_Error_authentication_failed")
      case "access_denied":
        return t("Login_Error_access_denied")
      case "remote_failure":
        return t("Login_Error_remote_failure")
      case "user_provisioning_failed":
        return t("Login_Error_user_provisioning_failed")
      default:
        return t("Login_Error_unknown")
    }
  })()

  return <Alert color="red">{message}</Alert>
}
